import { type ChangeEvent, type CSSProperties, type WheelEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Pause, Play, Shuffle, SkipBack, SkipForward, X } from 'lucide-react';
import FocusTimer from './components/FocusTimer';
import LiveStats from './components/LiveStats';
import TrackRow from './components/TrackRow';
import Visualizer from './components/Visualizer';
import { useLiveStats } from './hooks/useLiveStats';
import { usePlaybackTelemetry } from './hooks/usePlaybackTelemetry';
import { audioUrl, catalog, categories, categoryCounts, formatDuration, formatFocusTime, type Track } from './lib/catalog';

type FocusState = { total: number; remaining: number; running: boolean; startedAt: number | null };
type StoredPlayer = { trackId?: string; category?: string; shuffle?: boolean };

const focusStorageKey = 'brain-player-focus-v1';
const playerStorageKey = 'brain-player-player-v2';
const initialFocus: FocusState = { total: 50 * 60, remaining: 50 * 60, running: false, startedAt: null };

function readStored<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored ? { ...fallback as object, ...JSON.parse(stored) } as T : fallback;
  } catch {
    return fallback;
  }
}

function getInitialFocus(): FocusState {
  const stored = readStored(focusStorageKey, initialFocus);
  if (!stored.running || !stored.startedAt) return { ...stored, running: false, startedAt: null };
  const elapsed = Math.floor((Date.now() - stored.startedAt) / 1000);
  return { ...stored, remaining: stored.remaining - elapsed, running: false, startedAt: null };
}

function getStoredPlayer(): StoredPlayer {
  return readStored<StoredPlayer>(playerStorageKey, {});
}

function getInitialTrack(): Track {
  const stored = getStoredPlayer();
  return catalog.find((track) => track.track_id === stored.trackId) ?? catalog[0];
}

function getInitialCategory(): string {
  const stored = getStoredPlayer();
  return stored.category && categories.includes(stored.category) ? stored.category : 'All';
}

function describeError(error: unknown) {
  if (error instanceof Error) return { name: error.name, message: error.message };
  return { name: 'UnknownError', message: String(error) };
}

export default function App() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const shouldAutoplayRef = useRef(false);
  const categoryStripRef = useRef<HTMLDivElement>(null);
  const [currentTrack, setCurrentTrack] = useState<Track>(getInitialTrack);
  const [selectedCategory, setSelectedCategory] = useState(getInitialCategory);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isShuffle, setIsShuffle] = useState(() => Boolean(getStoredPlayer().shuffle));
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(currentTrack.duration_seconds);
  const [focus, setFocus] = useState<FocusState>(getInitialFocus);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const liveStats = useLiveStats(isPlaying);
  const logPlaybackEvent = usePlaybackTelemetry(audioRef, currentTrack, audioContextRef);
  const previousRemainingRef = useRef(focus.remaining);
  const notifiedOvertimeMilestoneRef = useRef(Math.floor(Math.max(0, -focus.remaining) / (30 * 60)));

  const filteredTracks = useMemo(
    () => selectedCategory === 'All' ? catalog : catalog.filter((track) => track.category === selectedCategory),
    [selectedCategory],
  );

  useEffect(() => {
    localStorage.setItem(playerStorageKey, JSON.stringify({
      trackId: currentTrack.track_id,
      category: selectedCategory,
      shuffle: isShuffle,
    }));
  }, [currentTrack.track_id, isShuffle, selectedCategory]);

  useEffect(() => {
    localStorage.setItem(focusStorageKey, JSON.stringify(focus));
  }, [focus]);

  const announceFocusMilestone = useCallback((isCompletion: boolean) => {
    const context = audioContextRef.current;
    if (context?.state === 'running') {
      const startAt = context.currentTime + 0.03;
      [523.25, 659.25].forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const noteStart = startAt + index * 0.16;
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, noteStart);
        gain.gain.setValueAtTime(0.0001, noteStart);
        gain.gain.exponentialRampToValueAtTime(0.035, noteStart + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + 0.34);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(noteStart);
        oscillator.stop(noteStart + 0.36);
      });
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(isCompletion ? 'Focus block complete' : 'Still in deep focus', {
          body: isCompletion
            ? 'Overtime has started. Your music is still playing.'
            : 'Another 30 minutes of overtime completed.',
          tag: isCompletion ? 'brain-player-focus-complete' : 'brain-player-overtime-reminder',
          silent: true,
        });
      } catch {
        // Some mobile browsers expose Notification but only support it through a service worker.
      }
    }
  }, []);

  useEffect(() => {
    const previousRemaining = previousRemainingRef.current;
    const overtimeMilestone = Math.floor(Math.max(0, -focus.remaining) / (30 * 60));

    if (focus.running && previousRemaining > 0 && focus.remaining <= 0) {
      announceFocusMilestone(true);
    }
    if (focus.remaining > 0) {
      notifiedOvertimeMilestoneRef.current = 0;
    } else if (focus.running && overtimeMilestone > notifiedOvertimeMilestoneRef.current) {
      notifiedOvertimeMilestoneRef.current = overtimeMilestone;
      announceFocusMilestone(false);
    } else if (!focus.running) {
      notifiedOvertimeMilestoneRef.current = overtimeMilestone;
    }
    previousRemainingRef.current = focus.remaining;
  }, [announceFocusMilestone, focus.remaining, focus.running]);

  useEffect(() => {
    document.title = focus.remaining <= 0
      ? `+${formatFocusTime(Math.abs(focus.remaining))} — Brain Player`
      : 'Brain Player — Deep Work Audio';
  }, [focus.remaining]);

  useEffect(() => {
    if (!focus.running || !focus.startedAt) return;
    const timer = window.setInterval(() => {
      setFocus((previous) => {
        if (!previous.running || !previous.startedAt) return previous;
        const elapsed = Math.max(1, Math.floor((Date.now() - previous.startedAt) / 1000));
        return { ...previous, remaining: previous.remaining - elapsed, startedAt: Date.now() };
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [focus.running, focus.startedAt]);

  useEffect(() => {
    if (!showLibrary) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowLibrary(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [showLibrary]);

  useEffect(() => {
    if (!showLibrary) return;
    const strip = categoryStripRef.current;
    const activeCategory = Array.from(strip?.querySelectorAll<HTMLButtonElement>('[data-category]') ?? [])
      .find((button) => button.dataset.category === selectedCategory);
    activeCategory?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
  }, [selectedCategory, showLibrary]);

  const beginFocus = useCallback(() => {
    setFocus((previous) => {
      if (previous.running) return previous;
      return {
        ...previous,
        running: true,
        startedAt: Date.now(),
      };
    });
  }, []);

  const pauseFocus = useCallback(() => {
    setFocus((previous) => {
      if (!previous.running || !previous.startedAt) return { ...previous, running: false, startedAt: null };
      const elapsed = Math.floor((Date.now() - previous.startedAt) / 1000);
      return { ...previous, remaining: previous.remaining - elapsed, running: false, startedAt: null };
    });
  }, []);

  const setupAudioGraph = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || sourceRef.current) return;
    try {
      const AudioContextClass = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;
      const context = new AudioContextClass();
      const source = context.createMediaElementSource(audio);
      const nextAnalyser = context.createAnalyser();
      nextAnalyser.fftSize = 256;
      nextAnalyser.smoothingTimeConstant = 0.84;
      source.connect(nextAnalyser);
      nextAnalyser.connect(context.destination);
      audioContextRef.current = context;
      sourceRef.current = source;
      context.addEventListener('statechange', () => {
        logPlaybackEvent('audio-context', { state: context.state });
      });
      logPlaybackEvent('audio-graph-ready', { state: context.state, sampleRate: context.sampleRate });
      setAnalyser(nextAnalyser);
    } catch (error) {
      // Playback remains available if a browser refuses the optional analyser graph.
      logPlaybackEvent('audio-graph-error', describeError(error));
    }
  }, [logPlaybackEvent]);

  const playAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setupAudioGraph();
    try {
      if (audioContextRef.current?.state === 'suspended') await audioContextRef.current.resume();
      await audio.play();
    } catch (error) {
      logPlaybackEvent('play-rejected', {
        ...describeError(error),
        audioContextState: audioContextRef.current?.state ?? null,
      });
      throw error;
    }
  }, [logPlaybackEvent, setupAudioGraph]);

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      if ('Notification' in window && Notification.permission === 'default') {
        void Notification.requestPermission().catch(() => undefined);
      }
      try {
        await playAudio();
      } catch {
        setIsPlaying(false);
        pauseFocus();
      }
    } else {
      audio.pause();
    }
  }, [pauseFocus, playAudio]);

  const selectTrack = useCallback((track: Track, autoplay = true) => {
    shouldAutoplayRef.current = autoplay;
    logPlaybackEvent('select-track', {
      trackId: track.track_id,
      category: track.category,
      autoplay,
    });
    setCurrentTrack(track);
    setCurrentTime(0);
    setDuration(track.duration_seconds);
    setShowLibrary(false);
  }, [logPlaybackEvent]);

  const stepTrack = useCallback((direction: 1 | -1) => {
    const candidates = filteredTracks.length > 0 ? filteredTracks : catalog;
    const currentIndex = candidates.findIndex((track) => track.track_id === currentTrack.track_id);
    if (isShuffle && candidates.length > 1) {
      let nextIndex = currentIndex;
      while (nextIndex === currentIndex) nextIndex = Math.floor(Math.random() * candidates.length);
      selectTrack(candidates[nextIndex]);
      return;
    }
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    selectTrack(candidates[(safeIndex + direction + candidates.length) % candidates.length]);
  }, [currentTrack.track_id, filteredTracks, isShuffle, selectTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = audioUrl(currentTrack);
    audio.load();
    if (shouldAutoplayRef.current) {
      shouldAutoplayRef.current = false;
      void playAudio().catch(() => {
        setIsPlaying(false);
        pauseFocus();
      });
    }
  }, [currentTrack, pauseFocus, playAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : currentTrack.duration_seconds);
    const onPlay = () => {
      setIsPlaying(true);
      beginFocus();
    };
    const onPause = () => {
      setIsPlaying(false);
      pauseFocus();
    };
    const onEnded = () => stepTrack(1);
    const onError = () => {
      setIsPlaying(false);
      pauseFocus();
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [beginFocus, currentTrack.duration_seconds, pauseFocus, stepTrack]);

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    const nextTime = Number(event.target.value);
    setCurrentTime(nextTime);
    if (audioRef.current) audioRef.current.currentTime = nextTime;
  };

  const resetFocus = () => setFocus((previous) => ({
    ...previous,
    remaining: previous.total,
    running: isPlaying,
    startedAt: isPlaying ? Date.now() : null,
  }));

  const setFocusDuration = (seconds: number) => setFocus({
    total: seconds,
    remaining: seconds,
    running: isPlaying,
    startedAt: isPlaying ? Date.now() : null,
  });

  const trackProgress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const sliderStyle = { '--track-progress': `${trackProgress}%` } as CSSProperties;

  const scrollCategories = (direction: -1 | 1) => {
    const strip = categoryStripRef.current;
    if (!strip) return;
    strip.scrollBy({
      left: direction * Math.max(260, strip.clientWidth * 0.72),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  const handleCategoryWheel = (event: WheelEvent<HTMLDivElement>) => {
    const strip = event.currentTarget;
    if (strip.scrollWidth <= strip.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    strip.scrollLeft += event.deltaY;
  };

  return (
    <div className={isPlaying ? 'app-shell is-playing' : 'app-shell'}>
      <header className="app-header">
        <a className="brand" href="/" aria-label="Brain Player home">
          <span className="brand-orb" />
          <span>brain player</span>
        </a>
        <button className="library-trigger" onClick={() => setShowLibrary(true)} aria-haspopup="dialog" aria-expanded={showLibrary}>
          <span>{currentTrack.category}</span>
          <ChevronDown size={15} />
        </button>
      </header>

      <main className="focus-screen">
        <section className="focus-stage" aria-label="Focus session">
          <div className="ambient-glow" />
          <Visualizer analyser={analyser} isPlaying={isPlaying} />
          <FocusTimer
            remaining={focus.remaining}
            total={focus.total}
            running={focus.running}
            onReset={resetFocus}
            onSetDuration={setFocusDuration}
          />
        </section>

        <section className="now-playing" aria-live="polite">
          <span className={isPlaying ? 'playing-indicator active' : 'playing-indicator'}><i /><i /><i /></span>
          <div>
            <h1>{currentTrack.name}</h1>
            <p>{currentTrack.category} · {currentTrack.activity || currentTrack.mental_state || 'Focus'}</p>
          </div>
        </section>

        <div className="track-progress">
          <span>{formatDuration(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={Math.max(1, duration)}
            step="0.1"
            value={Math.min(currentTime, duration || 0)}
            onChange={handleSeek}
            style={sliderStyle}
            aria-label="Track progress"
          />
          <span>{formatDuration(duration)}</span>
        </div>

        <div className="transport" aria-label="Playback controls">
          <button className={isShuffle ? 'transport-small active' : 'transport-small'} onClick={() => setIsShuffle((value) => !value)} aria-label="Toggle shuffle" aria-pressed={isShuffle}>
            <Shuffle size={18} />
          </button>
          <button className="transport-button" onClick={() => stepTrack(-1)} aria-label="Previous track">
            <SkipBack size={23} fill="currentColor" />
          </button>
          <button className="play-button" onClick={() => void togglePlayback()} aria-label={isPlaying ? 'Pause focus session' : 'Start focus session'}>
            {isPlaying ? <Pause size={26} fill="currentColor" /> : <Play size={27} fill="currentColor" />}
          </button>
          <button className="transport-button" onClick={() => stepTrack(1)} aria-label="Next track">
            <SkipForward size={23} fill="currentColor" />
          </button>
        </div>

        <p className="playback-hint">
          {isPlaying
            ? focus.remaining <= 0 ? 'Overtime · music continues' : 'Music and focus timer are running'
            : focus.remaining <= 0 ? 'Overtime paused' : 'Press play to begin your focus session'}
        </p>
        <LiveStats {...liveStats} />
      </main>

      {showLibrary && (
        <div className="sheet-backdrop" onClick={() => setShowLibrary(false)}>
          <section className="library-sheet" role="dialog" aria-modal="true" aria-label="Sound library" onClick={(event) => event.stopPropagation()}>
            <div className="sheet-handle" />
            <div className="sheet-header">
              <div>
                <span className="sheet-eyebrow">Sound library</span>
                <h2>Choose a sound</h2>
              </div>
              <button className="sheet-close" onClick={() => setShowLibrary(false)} aria-label="Close library"><X size={19} /></button>
            </div>

            <div className="category-strip-shell">
              <button className="category-scroll-button left" onClick={() => scrollCategories(-1)} aria-label="Scroll categories left"><ChevronLeft size={18} /></button>
              <div ref={categoryStripRef} className="category-strip" aria-label="Music categories" onWheel={handleCategoryWheel}>
                {categories.map((category) => (
                  <button
                    className={selectedCategory === category ? 'category-chip active' : 'category-chip'}
                    key={category}
                    data-category={category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                    <span>{category === 'All' ? catalog.length : categoryCounts[category]}</span>
                  </button>
                ))}
              </div>
              <button className="category-scroll-button right" onClick={() => scrollCategories(1)} aria-label="Scroll categories right"><ChevronRight size={18} /></button>
            </div>

            <div className="sheet-list-header">
              <span>{selectedCategory}</span>
              <small>{filteredTracks.length.toLocaleString()} tracks</small>
            </div>
            <div className="library-list">
              {filteredTracks.slice(0, 60).map((track) => (
                <TrackRow
                  key={track.track_id}
                  track={track}
                  active={track.track_id === currentTrack.track_id}
                  playing={isPlaying}
                  onSelect={selectTrack}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      <audio ref={audioRef} crossOrigin="anonymous" preload="metadata" />
    </div>
  );
}
